import * as L from 'leaflet';

type ValidLeafletMap = ReturnType<typeof L.map>;

export default class TouchToMouseMapper {
  private map: ValidLeafletMap;
  private container: HTMLElement;
  private isDrawing: boolean = false;

  constructor(map: ValidLeafletMap) {
    this.map = map;
    this.container = this.map.getContainer(); 
    this._initListeners();
  }

  public setDrawingMode(enabled: boolean): void {
    this.isDrawing = enabled;
    
    if (enabled) this.map.dragging.disable();
    else this.map.dragging.enable();
  }

  private _simulateMouseEvent = (mouseEventType: string, event: PointerEvent): void => {
    if (!this.isDrawing || event.pointerType === 'mouse') return;

    const target = event.target as HTMLElement | null;
    if (target && typeof target.closest === 'function') {
      if (
        target.tagName === 'BUTTON' || 
        target.tagName === 'A' || 
        target.closest('.leaflet-control') 
      ) {
        return; 
      }
    }

    if (event.cancelable) event.preventDefault(); 

    // Create the fake mouse event (PointerEvents have X/Y directly on them!)
    const simulatedEvent: MouseEvent = new MouseEvent(mouseEventType, {
      bubbles: true,
      cancelable: true,
      view: window,
      clientX: event.clientX,
      clientY: event.clientY,
      screenX: event.screenX,
      screenY: event.screenY,
      button: 0,  // "This is a left click"
      buttons: 1, // "The left click is actively being held down"
    });
    
    if (event.target) event.target.dispatchEvent(simulatedEvent);
  }

  // Map pointer events to mouse events
  private _handlePointerDown = (e: Event): void => { 
    this._simulateMouseEvent('mousedown', e as PointerEvent); 
  }
  
  private _handlePointerMove = (e: Event): void => { 
    this._simulateMouseEvent('mousemove', e as PointerEvent); 
  }
  
  private _handlePointerUp = (e: Event): void => { 
    this._simulateMouseEvent('mouseup', e as PointerEvent); 
  }

  private _initListeners(): void {
    // We use pointerdown/move/up instead of touchstart/move/end
    this.container.addEventListener('pointerdown', this._handlePointerDown, { passive: false });
    this.container.addEventListener('pointermove', this._handlePointerMove, { passive: false });
    this.container.addEventListener('pointerup', this._handlePointerUp, { passive: false });
    // Also catch pointercancel (e.g., if a phone call interrupts the screen touch)
    this.container.addEventListener('pointercancel', this._handlePointerUp, { passive: false });
  }

  public destroy(): void {
    this.container.removeEventListener('pointerdown', this._handlePointerDown);
    this.container.removeEventListener('pointermove', this._handlePointerMove);
    this.container.removeEventListener('pointerup', this._handlePointerUp);
    this.container.removeEventListener('pointercancel', this._handlePointerUp);
    this.map.dragging.enable();
  }
}